import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { adminService, BookingWithId } from '../services/adminService';
import { AdminCalendarView } from '../components/AdminCalendarView';

export function AdminDashboardPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<BookingWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showPastAppointments, setShowPastAppointments] = useState(false);
  const calendarRef = useRef<{ refresh: () => Promise<void> }>(null);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getAllBookings(50, showPastAppointments);
      setBookings(data);
    } catch (err) {
      setError('Failed to load bookings. Please try again.');
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  // Reload bookings when the toggle changes
  useEffect(() => {
    if (!loading) {
      loadBookings();
    }
  }, [showPastAppointments]);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const handleRefreshAll = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      // Refresh bookings and calendar simultaneously
      await Promise.all([
        loadBookings(),
        calendarRef.current?.refresh()
      ]);
    } catch (err) {
      setError('Failed to refresh data. Please try again.');
      console.error('Error refreshing data:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'completed':
        return 'outline';
      case 'cancelled':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getPaymentTypeBadge = (booking: BookingWithId) => {
    const isPaid = booking.checkoutId || booking.paymentStatus;
    
    if (isPaid) {
      const variant = booking.paymentStatus === 'paid' ? 'default' : 
                     booking.paymentStatus === 'pending' ? 'secondary' :
                     booking.paymentStatus === 'failed' ? 'destructive' : 'secondary';
      
      return (
        <Badge variant="outline">
          💰 Paid
        </Badge>
      );
    }
    
    return (
      <Badge variant="outline">
        🆓 Free
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8 pt-8">
          <div className="flex items-center gap-4">
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <Button 
              onClick={handleRefreshAll} 
              variant="outline" 
              size="sm"
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : '🔄 Refresh All'}
            </Button>
          </div>
          <Button onClick={handleLogout} variant="outline">
            Logout
          </Button>
        </div>

        <div className="grid gap-6 md:grid-cols-3 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Total Bookings</CardTitle>
              <CardDescription>All time bookings</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{bookings.length}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Pending</CardTitle>
              <CardDescription>Awaiting confirmation</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {bookings.filter(b => b.status === 'pending').length}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Confirmed</CardTitle>
              <CardDescription>Upcoming appointments</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {bookings.filter(b => b.status === 'confirmed').length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Slot Management Section */}
        <div className="mb-8">
          <AdminCalendarView ref={calendarRef} />
        </div>

        {/* Bookings Section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Recent Bookings</CardTitle>
              <CardDescription>
                {showPastAppointments 
                  ? 'All appointments and consultations (including past)' 
                  : 'Upcoming appointments and consultations only'
                }
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowPastAppointments(!showPastAppointments)}
                variant={showPastAppointments ? "default" : "outline"}
                size="sm"
              >
                {showPastAppointments ? 'Hide Past' : 'Show Past'}
              </Button>
              <Button onClick={loadBookings} variant="outline" size="sm">
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Loading bookings...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600">{error}</p>
                <Button onClick={loadBookings} variant="outline" className="mt-4">
                  Try Again
                </Button>
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No bookings found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date & Time</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Payment Type</TableHead>
                        <TableHead>Contact Info</TableHead>
                        <TableHead>Duration</TableHead>
                      </TableRow>
                    </TableHeader>
                  <TableBody>
                    {bookings.map((booking) => (
                      <TableRow key={booking.id}>
                        <TableCell className="font-medium">
                          {booking.scheduledDateTimeLA || adminService.formatDate(booking.scheduledDateTime)}
                          <div className="text-xs text-gray-500">{booking.timezone}</div>
                        </TableCell>
                        <TableCell>
                          <div>{booking.customerInfo.firstName}</div>
                          <div className="text-xs text-gray-500">{booking.customerInfo.email}</div>
                        </TableCell>
                        <TableCell>
                          {adminService.getServiceLabel(booking.serviceType)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(booking.status)}>
                            {booking.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {getPaymentTypeBadge(booking)}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {/* Zoom Meeting Info */}
                            {booking.preferences.communicationChannel === 'zoom' && (
                              <div className="text-sm">
                                <div className="font-medium">📹 Zoom Meeting</div>
                                {booking.meeting?.zoomUrl ? (
                                  <a 
                                    href={booking.meeting.zoomUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline text-xs break-all"
                                  >
                                    Join Meeting
                                  </a>
                                ) : (
                                  <span className="text-xs text-gray-500">Meeting URL pending</span>
                                )}
                                {booking.meeting?.zoomMeetingId && (
                                  <div className="text-xs text-gray-600">
                                    ID: {booking.meeting.zoomMeetingId}
                                  </div>
                                )}
                                {booking.meeting?.zoomPassword && (
                                  <div className="text-xs text-gray-600">
                                    Password: {booking.meeting.zoomPassword}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Phone Info */}
                            {booking.preferences.communicationChannel === 'phone' && (
                              <div className="text-sm">
                                <div className="font-medium">📞 Phone Call</div>
                                {booking.customerInfo.phone ? (
                                  <a 
                                    href={`tel:${booking.customerInfo.phone}`}
                                    className="text-blue-600 hover:text-blue-800 text-xs"
                                  >
                                    {booking.customerInfo.phone}
                                  </a>
                                ) : (
                                  <span className="text-xs text-gray-500">No phone provided</span>
                                )}
                              </div>
                            )}
                            
                            {/* WeChat Info */}
                            {booking.preferences.communicationChannel === 'wechat' && (
                              <div className="text-sm">
                                <div className="font-medium">💬 WeChat</div>
                                {booking.customerInfo.wechatId ? (
                                  <div className="text-xs text-gray-700 font-mono">
                                    {booking.customerInfo.wechatId}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-500">No WeChat ID provided</span>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{booking.duration} min</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
